<script setup lang="ts">
import { computed } from 'vue';
import { useAuthStore } from '@/stores/auth';
import VerificationStatusBadge from '@/components/VerificationStatusBadge.vue';

definePage({ meta: { requiresAuth: true } });

const auth = useAuthStore();

const canManage = computed(
    () => auth.providerUser?.role === 'owner' || auth.providerUser?.role === 'admin',
);

/**
 * Punto de entrada a Settings. Duplica el submenú a propósito: el menú sirve
 * para saltar cuando ya sabés a dónde vas, y esto para descubrir qué hay.
 * Acá también se anuncian las secciones que todavía no existen, en vez de
 * ensuciar el menú con items muertos.
 */
interface SettingsSection {
    title: string;
    description: string;
    icon: string;
    to?: { name: string };
    /** Rojo para lo destructivo. Se declara aunque hoy no lo use ninguna. */
    color?: string;
    disabled?: boolean;
    disabledHint?: string;
    comingSoon?: boolean;
}

const sections = computed<SettingsSection[]>(() => [
    {
        title: 'Company',
        description: 'Your business details and the danger zone.',
        icon: 'ri-building-line',
        to: { name: 'settings-company' },
    },
    {
        title: 'Profile',
        description: 'Your personal details and your password.',
        icon: 'ri-user-line',
        to: { name: 'settings-profile' },
    },
    {
        title: 'Team',
        description: 'Invite people and manage their roles and access.',
        icon: 'ri-team-line',
        to: { name: 'settings-team' },
        disabled: !canManage.value,
        disabledHint: 'Owners and admins only',
    },
    {
        title: 'Medical questionnaire',
        description: 'What your divers declare about their health before getting in the water.',
        icon: 'ri-heart-pulse-line',
        to: { name: 'settings-medical-questionnaire' },
    },
    {
        title: 'Preferences',
        description: 'Dates, numbers, currency and units across the portal.',
        icon: 'ri-global-line',
        to: { name: 'settings-preferences' },
    },
    {
        title: 'Theme',
        description: 'Light or dark, layout and content width on this device.',
        icon: 'ri-palette-line',
        to: { name: 'settings-theme' },
    },
    {
        // Apagada por ahora (§60), como el item del menú: el cobro por volumen
        // todavía no se anuncia. `comingSoon` es el mismo tratamiento que ya
        // tenía Integrations, así que no hace falta nada nuevo.
        title: 'Subscription',
        description: 'The selected dive center\'s plan, and how much of it is used.',
        icon: 'ri-bank-card-line',
        comingSoon: true,
    },
    {
        title: 'Integrations',
        description: 'Connect Blue Trails with the tools you already use.',
        icon: 'ri-link',
        comingSoon: true,
    },
]);
</script>

<template>
  <div>
    <!-- Sin título de página (lo dice el menú): queda el estado de la cuenta,
         alineado a la derecha como estaba dentro del encabezado. -->
    <div class="d-flex justify-end mb-6">
      <verification-status-badge
        v-if="auth.provider"
        :status="auth.provider.verificationStatus"
      />
    </div>

    <v-row>
      <v-col
        v-for="section in sections"
        :key="section.title"
        cols="12"
        sm="6"
        lg="4"
      >
        <v-card
          border
          flat
          class="pa-6 h-100 d-flex flex-column"
          :class="{ 'opacity-50': section.comingSoon }"
        >
          <div class="d-flex align-center ga-3 mb-3">
            <v-avatar
              :color="section.color ?? 'primary'"
              variant="tonal"
              rounded="lg"
              size="42"
            >
              <v-icon :icon="section.icon" size="22" />
            </v-avatar>
            <h2
              class="text-h6 font-weight-bold"
              :class="section.color ? `text-${section.color}` : ''"
            >
              {{ section.title }}
            </h2>
          </div>

          <p class="text-body-2 text-medium-emphasis mb-4">
            {{ section.description }}
          </p>

          <!-- mt-auto alinea todos los botones abajo aunque las descripciones
               tengan distinto largo. -->
          <div class="mt-auto">
            <v-btn
              v-if="section.comingSoon"
              variant="outlined"
              color="secondary"
              disabled
              block
            >
              Coming soon
            </v-btn>
            <v-tooltip v-else-if="section.disabled" :text="section.disabledHint" location="top">
              <template #activator="{ props }">
                <!-- El botón deshabilitado no emite eventos, así que el tooltip
                     va en un wrapper; si no, no habría forma de saber por qué
                     está bloqueado. -->
                <div v-bind="props">
                  <v-btn variant="outlined" color="secondary" disabled block>
                    {{ section.title }}
                  </v-btn>
                </div>
              </template>
            </v-tooltip>
            <v-btn
              v-else
              :to="section.to"
              :color="section.color ?? 'primary'"
              variant="outlined"
              block
            >
              Open {{ section.title }}
            </v-btn>
          </div>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>
